/*
 * @Author: Auto
 * @Date: 2025-01-XX
 * @Description: 图片缓存 composable，用于避免同一图片在禁用缓存时重复加载
 */

// 全局图片缓存 Map
const imageCache = new Map<string, string>();

/**
 * 预加载图片并返回缓存 URL（Blob URL）
 * @param src 图片源路径
 * @returns Promise<string> 返回图片 URL（可能是原始 URL 或 Blob URL）
 */
async function loadImageWithCache(src: string): Promise<string> {
    // 如果已经在缓存中，直接返回
    if (imageCache.has(src)) {
        return imageCache.get(src)!;
    }

    // 如果不是客户端环境，直接返回原始 URL
    if (typeof window === 'undefined') {
        return src;
    }

    try {
        // 使用 fetch 加载图片
        const response = await fetch(src);
        if (!response.ok) {
            throw new Error(`Failed to load image: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // 缓存 blob URL
        imageCache.set(src, blobUrl);
        
        return blobUrl;
    } catch (error) {
        // 加载失败时，返回原始 URL 作为降级方案
        console.warn(`Failed to cache image ${src}:`, error);
        return src;
    }
}

/**
 * 图片缓存 composable
 */
export function useImageCache() {
    /**
     * 获取缓存的图片 URL
     * @param src 图片源路径
     * @returns 返回缓存的图片 URL（如果已缓存）或原始 URL
     */
    const getCachedImage = async (src: string): Promise<string> => {
        if (!src) return '';
        return await loadImageWithCache(src);
    };

    /**
     * 预加载图片（不等待结果）
     * @param src 图片源路径
     */
    const preloadImage = (src: string) => {
        if (!src || typeof window === 'undefined') return;
        loadImageWithCache(src).catch(() => {
            // 静默失败，不阻塞
        });
    };

    /**
     * 清除指定图片的缓存
     * @param src 图片源路径
     */
    const clearCache = (src: string) => {
        const cachedUrl = imageCache.get(src);
        if (cachedUrl && cachedUrl.startsWith('blob:')) {
            URL.revokeObjectURL(cachedUrl);
        }
        imageCache.delete(src);
    };

    /**
     * 清除所有缓存
     */
    const clearAllCache = () => {
        imageCache.forEach((blobUrl) => {
            if (blobUrl.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrl);
            }
        });
        imageCache.clear();
    };

    return {
        getCachedImage,
        preloadImage,
        clearCache,
        clearAllCache,
    };
}

